<template>
  <div class="split-screen-container">
    <div class="visual-section">
      <div class="image-wrapper">
        <div class="visual-slider">
          <transition :name="transitionName" mode="out-in">
            <div :key="currentVisualMediaKey" class="media-frame">
              <img
                v-if="currentVisualMediaType === 'image'"
                :src="currentVisualMediaUrl"
                alt="Onekids"
                class="main-image"
                @error="handleVisualMediaError"
              />
              <iframe
                v-else-if="currentVisualMediaType === 'youtube'"
                ref="youtubeIframe"
                :src="currentVisualMediaUrl"
                class="main-video"
                title="OneKids visual video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
              <video
                v-else
                ref="nativeVideo"
                :src="currentVisualMediaUrl"
                class="main-video"
                controls
                preload="metadata"
                playsinline
                @play="handleNativeVideoPlay"
                @pause="handleNativeVideoPause"
                @ended="handleVisualVideoEnded"
                @error="handleVisualMediaError"
              ></video>
            </div>
          </transition>

          <button
            v-if="visualMediaItems.length > 1 && currentVisualMediaType !== 'image'"
            type="button"
            class="skip-video-btn"
            @click="skipCurrentVideo"
          >
            Bỏ qua video
          </button>

          <div
            v-if="visualMediaItems.length > 1"
            class="visual-indicators"
            aria-label="Trạng thái media minh họa"
          >
            <button
              v-for="(mediaItem, index) in visualMediaItems"
              :key="`${mediaItem.url}-${mediaItem.type}-${index}`"
              type="button"
              class="indicator-dot"
              :class="{ active: index === currentVisualImageIndex }"
              :aria-label="`Chuyển tới media ${index + 1}`"
              @click="goToImage(index)"
            ></button>
          </div>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="form-container">
        <el-form
            :label-position="labelPosition"
            :rules="rules"
            label-width="100px"
            :model="credentials"
            ref="credentials"
            class="login-standard-form"
        >
          <div class="brand-header">
            <img src="@/assets/images/onekids-logo.png" alt="Logo Onekids" class="login-logo" />
            <div class="title-login">ĐĂNG NHẬP</div>
          </div>

          <el-form-item label="Tên khoản" prop="username">
            <el-input
                prefix-icon="el-icon-phone-outline"
                v-model.trim="credentials.username"
                @keyup.enter.native="submitForm('credentials')"
                autocomplete="username"
                placeholder="Nhập số điện thoại"
            ></el-input>
          </el-form-item>

          <el-form-item label="Mật khẩu" prop="password">
            <el-input
                prefix-icon="el-icon-lock"
                v-model.trim="credentials.password"
                @keyup.enter.native="submitForm('credentials')"
                placeholder="Nhập mật khẩu"
                show-password
            ></el-input>
          </el-form-item>

          <el-form-item prop="appType" class="app-type-selector">
            <el-radio-group v-model="credentials.appType">
              <el-radio label="plus">Nhà trường</el-radio>
              <el-radio label="teacher">Giáo viên</el-radio>
              <el-radio label="parent">Phụ huynh</el-radio>
            </el-radio-group>
          </el-form-item>

          <div
              class="error"
              :style="{ visibility: showBadCredential ? 'visible' : 'hidden' }"
          >
            {{ loginError }}
          </div>

          <el-button
              type="primary"
              @click="submitForm('credentials')"
              :loading="loadingButton"
              class="submit-btn"
              round
          >Đăng nhập</el-button
          >

          <div class="note">
            <span class="forgot-link">Liên hệ với nhà trường khi quên mật khẩu</span>
            <hr class="form-hr" />
            <span class="slogan-text">Trẻ em hôm nay, thế giới ngày mai!</span>
          </div>

          <div class="social-links-wrapper">
            <span class="social-links-title">Kết nối với OneKids</span>

            <div class="social-links">
              <button
                type="button"
                class="social-link social-link--onekids"
                aria-label="Mở website OneKids"
                @click="openExternalLink(socialLinks.onekids)"
              >
                <img
                  src="@/assets/images/onekids-logo.png"
                  alt="OneKids"
                  class="social-link-logo"
                />
              </button>

              <button
                type="button"
                class="social-link social-link--facebook"
                aria-label="Mở Facebook OneKids"
                @click="openExternalLink(socialLinks.facebook)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg">
                  <path
                    d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.3-1.6 1.6-1.6H17V3.8c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4V10H8v3h2.8v8h2.7Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              <button
                type="button"
                class="social-link social-link--zalo"
                aria-label="Chat ngay với OneKids trên Zalo"
                @click="openZaloContact(primaryZaloPhone)"
              >
                <svg viewBox="0 0 64 64" aria-hidden="true" class="social-icon-svg social-icon-svg--zalo">
                  <rect x="8" y="10" width="48" height="44" rx="14" fill="currentColor" />
                  <path
                    d="M22 24h20v4L28.5 40H42v4H22v-4L35.5 28H22v-4Zm8 26 2.8-4h6.7L32 54l-2-4Z"
                    fill="#ffffff"
                  />
                </svg>
              </button>

              <button
                type="button"
                class="social-link social-link--youtube"
                aria-label="Xem kênh YouTube OneKids"
                @click="openExternalLink(socialLinks.youtube)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg">
                  <path
                    d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="app-download-wrapper">
<!--            <span class="app-download-title">Tải ứng dụng OneKids</span>-->

            <div class="app-download-actions">
              <button
                type="button"
                class="app-download-link app-download-link--play"
                aria-label="Tải ứng dụng OneKids trên Google Play"
                @click="openExternalLink(socialLinks.googlePlay)"
              >
                <span class="app-download-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="store-icon-svg">
                    <path
                      d="M4.7 3.6a1 1 0 0 0-.5.9v15a1 1 0 0 0 .5.9l8.7-8.4-8.7-8.4Zm10 9.6 2.7-2.6L7.2 4.7l7.5 8.5Zm3.5-3.4 2.6-1.5c1-.6 1-1.9 0-2.5l-2.9-1.7-2.8 2.7 3.1 3Zm-2.9 4.4 2.8 2.7 2.9-1.7c1-.6 1-1.9 0-2.5l-2.6-1.5-3.1 3Zm-8.1 5.1 10.2-5.9-2.7-2.6-7.5 8.5Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="app-download-text">
                  <span class="app-download-sub">Tải trên</span>
                  <span class="app-download-name">Google Play</span>
                </span>
              </button>

              <button
                type="button"
                class="app-download-link app-download-link--apple"
                aria-label="Tải ứng dụng OneKids trên App Store"
                @click="openExternalLink(socialLinks.appStore)"
              >
                <span class="app-download-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true" class="store-icon-svg">
                    <path
                      d="M15.3 3.2c.1 1-.3 2-1 2.8-.8.8-1.9 1.3-2.9 1.2-.1-1 .3-2 1-2.7.8-.8 2-1.3 2.9-1.3ZM18.7 17.6c-.5 1.1-.8 1.6-1.4 2.6-.8 1.3-1.9 2.9-3.3 2.9-1.2 0-1.5-.8-3.1-.8-1.6 0-2 .8-3.2.8-1.4 0-2.4-1.4-3.2-2.7-2.2-3.3-2.5-7.2-1.1-9.4 1-1.6 2.6-2.6 4.1-2.6 1.6 0 2.6.8 3.9.8 1.3 0 2.1-.8 3.9-.8 1.4 0 2.9.8 3.9 2.2-3.4 1.9-2.9 6.8.5 8Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="app-download-text">
                  <span class="app-download-sub">Tải trên</span>
                  <span class="app-download-name">App Store</span>
                </span>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="copyright-link"
            @click="openExternalLink(socialLinks.developer)"
          >
            Copyright 2017 Developer by OneGroup VietNam
          </button>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import baseUrl from "@/base-url";
import DeviceService from "@/services/DeviceService";

const LOGIN_CONFIGURATION_URL = `${baseUrl.url}/web/api/public/login-configuration`;

const defaultVisualMediaItems = [
  {
    type: "image",
    url: "https://media.onekids.edu.vn/sysfiles/login/onekids_login.png",
  },
  {
    type: "image",
    url: "https://media.onekids.edu.vn/sysfiles/login/onekids_plus_login.png",
  },
  {
    type: "image",
    url: "https://media.onekids.edu.vn/sysfiles/login/onekids_teacher_login.png",
  },
  {
    type: "image",
    url: "https://media.onekids.edu.vn/sysfiles/login/onekids_parent_login.png",
  },
  {
    type: "image",
    url: "https://media.onekids.edu.vn/sysfiles/login/onecam_plus.png",
  },
];

const defaultSocialLinks = {
  onekids: "https://onekids.edu.vn/",
  facebook: "https://www.facebook.com/onekids.edu.vn",
  youtube: "https://www.youtube.com/@onekidsvietnam",
  googlePlay: "https://onekids.edu.vn/tai-ung-dung/",
  appStore: "https://onekids.edu.vn/tai-ung-dung/",
  developer: "https://onegroupvn.com/",
};

const defaultZaloContacts = [
  { name: "OneKids Việt Nam", phone: "0392829001" },
];

export default {
  data() {
    const validateUsername = (rule, value, callback) => {
      if (value.trim() === "") {
        callback(new Error("Nhập số điện thoại"));
      } else {
        callback();
      }
    };

    const validatePassword = (rule, value, callback) => {
      if (value.trim() === "") {
        callback(new Error("Nhập mật khẩu"));
      } else {
        callback();
      }
    };
    return {
      labelPosition: "top",
      showBadCredential: false,
      loadingButton: false,
      loginError: "Thông tin đăng nhập không đúng",
      visualMediaItems: [...defaultVisualMediaItems],
      socialLinks: { ...defaultSocialLinks },
      zaloContacts: [...defaultZaloContacts],
      cachedVisualImageUrls: {},
      currentVisualImageIndex: 0,
      failedVisualImageIndexes: [],
      visualObjectUrls: [],
      visualImageCacheName: "onekids-login-visual-images-v1",
      transitionName: "visual-slide-next",
      imageRotationInterval: 5000,
      imageRotationTimer: null,
      youtubePlayer: null,
      youtubePlayerMediaKey: "",
      youtubeApiPromise: null,
      isCurrentVideoPlaying: false,
      credentials: {
        username: "",
        password: "",
        appType: "",
      },
      rules: {
        username: [
          { validator: validateUsername, trigger: "blur" },
          { validator: validateUsername, trigger: "change" },
        ],
        password: [
          { validator: validatePassword, trigger: "blur" },
          { validator: validatePassword, trigger: "change" },
          {
            min: 6,
            message: "Mật khẩu phải lớn hơn hoặc bằng 6 ký tự",
            trigger: "blur",
          },
        ],
      },
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    primaryZaloPhone() {
      const firstContact = this.zaloContacts[0] || null;
      return firstContact && firstContact.phone ? firstContact.phone : "";
    },
    currentVisualMedia() {
      return this.visualMediaItems[this.currentVisualImageIndex] || this.visualMediaItems[0] || null;
    },
    currentVisualMediaType() {
      if (!this.currentVisualMedia) {
        return "image";
      }

      if (this.currentVisualMedia.type !== "video") {
        return "image";
      }

      return this.getYoutubeEmbedUrl(this.currentVisualMedia.url) ? "youtube" : "video";
    },
    currentVisualMediaUrl() {
      if (!this.currentVisualMedia) {
        return "";
      }

      if (this.currentVisualMedia.type === "image") {
        return (
          this.cachedVisualImageUrls[this.currentVisualImageIndex] ||
          this.currentVisualMedia.url ||
          ""
        );
      }

      if (this.currentVisualMediaType === "youtube") {
        return this.getYoutubeEmbedUrl(this.currentVisualMedia.url) || "";
      }

      return this.currentVisualMedia.url || "";
    },
    currentVisualMediaKey() {
      if (!this.currentVisualMedia) {
        return "empty-media";
      }

      return `${this.currentVisualMedia.type}-${this.currentVisualImageIndex}-${this.currentVisualMedia.url}`;
    },
  },
  watch: {
    currentVisualMediaKey() {
      this.isCurrentVideoPlaying = false;
      this.syncRotationForCurrentMedia();
    },
  },
  created() {
    if (this.loggedIn) {
      this.$router.push("/dashboard");
    }
  },
  mounted() {
    this.initVisualImageCache();
    this.syncRotationForCurrentMedia();
    this.loadLoginConfiguration();
  },
  beforeDestroy() {
    this.clearImageRotation();
    this.destroyYoutubePlayer();
    this.cleanupVisualObjectUrls();
  },
  methods: {
    loadLoginConfiguration() {
      return axios
        .get(LOGIN_CONFIGURATION_URL)
        .then((response) => {
          const apiData = response && response.data ? response.data.data || {} : {};
          const mappedVisualMedia = this.mapVisualMedia(apiData.visualMedia);
          const mappedSocialLinks = this.mapSocialLinks(apiData);
          const mappedZaloContacts = this.mapZaloContacts(apiData.phoneZalo);

          if (mappedVisualMedia.length > 0) {
            this.visualMediaItems = mappedVisualMedia;
            this.currentVisualImageIndex = 0;
            this.failedVisualImageIndexes = [];
            this.cachedVisualImageUrls = {};
            this.cleanupVisualObjectUrls();
            this.initVisualImageCache();
          }

          this.socialLinks = {
            ...this.socialLinks,
            ...mappedSocialLinks,
          };

          if (mappedZaloContacts.length > 0) {
            this.zaloContacts = mappedZaloContacts;
          }

          this.syncRotationForCurrentMedia();
        })
        .catch(() => null);
    },
    mapVisualMedia(visualMedia) {
      if (!Array.isArray(visualMedia) || visualMedia.length === 0) {
        return [];
      }

      return visualMedia
        .map((item) => {
          const url = this.normalizeUrl(item && item.url);
          const type = this.normalizeVisualMediaType(item && item.type);
          const orderPriority = Number(item && item.orderPriority);

          if (!url || !type) {
            return null;
          }

          return {
            type,
            url,
            orderPriority: Number.isFinite(orderPriority) ? orderPriority : Number.MAX_SAFE_INTEGER,
          };
        })
        .filter(Boolean)
        .sort((leftItem, rightItem) => leftItem.orderPriority - rightItem.orderPriority)
        .map(({ type, url }) => ({ type, url }));
    },
    mapSocialLinks(apiData) {
      const socialLinks = (apiData && apiData.socialLinks) || {};
      const downloadLinks = (apiData && apiData.downloadLinks) || {};

      return {
        facebook: this.normalizeUrl(socialLinks.facebook_url) || this.socialLinks.facebook,
        youtube: this.normalizeUrl(socialLinks.youtube_url) || this.socialLinks.youtube,
        googlePlay:
          this.normalizeUrl(downloadLinks.google_play_store_url) || this.socialLinks.googlePlay,
        appStore: this.normalizeUrl(downloadLinks.app_store_url) || this.socialLinks.appStore,
        developer: this.normalizeUrl(apiData && apiData.copyrightText) || this.socialLinks.developer,
      };
    },
    mapZaloContacts(phoneZalo) {
      const zaloPhone = this.normalizePhoneNumber(phoneZalo && phoneZalo.zalo_phone);
      if (!zaloPhone) {
        return [];
      }

      return [{ name: "OneKids Việt Nam", phone: zaloPhone }];
    },
    normalizeVisualMediaType(type) {
      const normalizedType = String(type || "").trim().toLowerCase();
      if (normalizedType === "image") {
        return "image";
      }

      if (normalizedType === "video" || normalizedType === "youtube") {
        return "video";
      }

      return "";
    },
    normalizeUrl(url) {
      const normalizedUrl = String(url || "").trim();
      if (!normalizedUrl) {
        return "";
      }

      return this.isValidHttpUrl(normalizedUrl) ? normalizedUrl : "";
    },
    isValidHttpUrl(url) {
      try {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      } catch (error) {
        return false;
      }
    },
    getYoutubeEmbedUrl(url) {
      const videoId = this.extractYoutubeVideoId(url);
      if (!videoId) {
        return "";
      }

      const origin =
        typeof window !== "undefined" && window.location && window.location.origin
          ? `&origin=${encodeURIComponent(window.location.origin)}`
          : "";
      const params = `enablejsapi=1&autoplay=0&rel=0&modestbranding=1${origin}`;
      return `https://www.youtube.com/embed/${videoId}?${params}`;
    },
    extractYoutubeVideoId(url) {
      if (!url || typeof url !== "string") {
        return "";
      }

      try {
        const parsedUrl = new URL(url);
        const host = parsedUrl.hostname.replace(/^www\./, "");

        if (host === "youtu.be") {
          return (parsedUrl.pathname || "").replace("/", "");
        }

        if (host !== "youtube.com" && host !== "m.youtube.com") {
          return "";
        }

        if (parsedUrl.pathname === "/watch") {
          return parsedUrl.searchParams.get("v") || "";
        }

        if (parsedUrl.pathname.startsWith("/shorts/")) {
          return parsedUrl.pathname.split("/")[2] || "";
        }

        if (parsedUrl.pathname.startsWith("/embed/")) {
          return parsedUrl.pathname.split("/")[2] || "";
        }

        return "";
      } catch (error) {
        return "";
      }
    },
    syncRotationForCurrentMedia() {
      if (this.currentVisualMediaType === "image") {
        this.isCurrentVideoPlaying = false;
        this.destroyYoutubePlayer();
        this.startImageRotation();
        return;
      }

      if (this.currentVisualMediaType === "youtube") {
        this.initYoutubePlayer();
      } else {
        this.destroyYoutubePlayer();
      }

      if (this.isCurrentVideoPlaying) {
        this.clearImageRotation();
      } else {
        this.startImageRotation();
      }
    },
    loadYoutubeIframeApi() {
      if (typeof window === "undefined") {
        return Promise.reject(new Error("Không có window"));
      }

      if (window.YT && window.YT.Player) {
        return Promise.resolve(window.YT);
      }

      if (this.youtubeApiPromise) {
        return this.youtubeApiPromise;
      }

      this.youtubeApiPromise = new Promise((resolve) => {
        const previousReadyHandler = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof previousReadyHandler === "function") {
            previousReadyHandler();
          }
          resolve(window.YT);
        };

        const existingScript = document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]',
        );

        if (!existingScript) {
          const script = document.createElement("script");
          script.src = "https://www.youtube.com/iframe_api";
          document.body.appendChild(script);
        }
      });

      return this.youtubeApiPromise;
    },
    initYoutubePlayer() {
      if (this.currentVisualMediaType !== "youtube") {
        return;
      }

      this.$nextTick(() => {
        const iframeElement = this.$refs.youtubeIframe;
        if (!iframeElement) {
          return;
        }

        this.loadYoutubeIframeApi()
          .then((YT) => {
            if (!YT || !YT.Player) {
              return;
            }

            if (this.youtubePlayer && this.youtubePlayerMediaKey === this.currentVisualMediaKey) {
              return;
            }

            this.destroyYoutubePlayer();
            this.youtubePlayer = new YT.Player(iframeElement, {
              events: {
                onStateChange: this.handleYoutubeStateChange,
                onError: this.handleVisualMediaError,
              },
            });
            this.youtubePlayerMediaKey = this.currentVisualMediaKey;
          })
          .catch(() => {
            // Bỏ qua lỗi load API để không chặn luồng login.
          });
      });
    },
    destroyYoutubePlayer() {
      if (this.youtubePlayer && typeof this.youtubePlayer.destroy === "function") {
        this.youtubePlayer.destroy();
      }
      this.youtubePlayer = null;
      this.youtubePlayerMediaKey = "";
    },
    handleYoutubeStateChange(event) {
      if (!window.YT || !window.YT.PlayerState) {
        return;
      }

      if (event.data === window.YT.PlayerState.PLAYING) {
        this.isCurrentVideoPlaying = true;
        this.clearImageRotation();
        return;
      }

      if (
        event.data === window.YT.PlayerState.UNSTARTED ||
        event.data === window.YT.PlayerState.PAUSED ||
        event.data === window.YT.PlayerState.CUED
      ) {
        this.isCurrentVideoPlaying = false;
        this.startImageRotation();
        return;
      }

      if (event.data === window.YT.PlayerState.ENDED) {
        this.isCurrentVideoPlaying = false;
        this.showNextImage();
      }
    },
    handleNativeVideoPlay() {
      this.isCurrentVideoPlaying = true;
      this.clearImageRotation();
    },
    handleNativeVideoPause() {
      this.isCurrentVideoPlaying = false;
      this.startImageRotation();
    },
    skipCurrentVideo() {
      if (this.currentVisualMediaType === "video") {
        const nativeVideoElement = this.$refs.nativeVideo;
        if (nativeVideoElement && typeof nativeVideoElement.pause === "function") {
          nativeVideoElement.pause();
        }
      }

      if (
        this.currentVisualMediaType === "youtube" &&
        this.youtubePlayer &&
        typeof this.youtubePlayer.stopVideo === "function"
      ) {
        this.youtubePlayer.stopVideo();
      }

      this.isCurrentVideoPlaying = false;
      this.showNextImage();
    },
    handleVisualVideoEnded() {
      this.isCurrentVideoPlaying = false;
      this.showNextImage();
    },
    initVisualImageCache() {
      if (!Array.isArray(this.visualMediaItems) || this.visualMediaItems.length === 0) {
        return Promise.resolve();
      }

      const cacheTasks = this.visualMediaItems.map((mediaItem, index) => {
        if (!mediaItem || mediaItem.type !== "image") {
          return Promise.resolve();
        }

        return this.resolveVisualImage(mediaItem.url, index).catch(() => null);
      });

      return Promise.all(cacheTasks);
    },
    resolveVisualImage(imageUrl, index) {
      if (this.cachedVisualImageUrls[index]) {
        return Promise.resolve(this.cachedVisualImageUrls[index]);
      }

      return this.getCachedVisualImageUrl(imageUrl)
        .then((resolvedImageUrl) => {
          if (!resolvedImageUrl) {
            return imageUrl;
          }

          this.$set(this.cachedVisualImageUrls, index, resolvedImageUrl);
          return resolvedImageUrl;
        })
        .catch(() => imageUrl);
    },
    getCachedVisualImageUrl(imageUrl) {
      if (
        typeof window === "undefined" ||
        !window.fetch ||
        !window.URL ||
        !window.URL.createObjectURL ||
        !window.caches
      ) {
        return Promise.resolve(imageUrl);
      }

      return window.caches
        .open(this.visualImageCacheName)
        .then((cache) => {
          return cache.match(imageUrl).then((cachedResponse) => {
            if (cachedResponse) {
              return cachedResponse;
            }

            return window.fetch(imageUrl, { cache: "force-cache", mode: "cors" }).then(
              (networkResponse) => {
                if (!networkResponse || !networkResponse.ok) {
                  throw new Error("Không thể tải ảnh minh họa");
                }

                cache.put(imageUrl, networkResponse.clone());
                return networkResponse;
              },
            );
          });
        })
        .then((response) => response.blob())
        .then((blob) => {
          const objectUrl = window.URL.createObjectURL(blob);
          this.visualObjectUrls.push(objectUrl);
          return objectUrl;
        })
        .catch(() => imageUrl);
    },
    cleanupVisualObjectUrls() {
      if (typeof window === "undefined" || !window.URL || !window.URL.revokeObjectURL) {
        return;
      }

      this.visualObjectUrls.forEach((objectUrl) => {
        window.URL.revokeObjectURL(objectUrl);
      });
      this.visualObjectUrls = [];
    },
    openExternalLink(url) {
      const normalizedUrl = this.normalizeUrl(url);
      if (!normalizedUrl) {
        return;
      }

      window.open(normalizedUrl, "_blank", "noopener,noreferrer");
    },
    normalizePhoneNumber(phone) {
      return String(phone || "").replace(/[^\d]/g, "");
    },
    openZaloContact(phone) {
      const normalizedPhone = this.normalizePhoneNumber(phone);
      if (!normalizedPhone) {
        return;
      }

      const zaloUrl = `https://zalo.me/${normalizedPhone}`;
      window.open(zaloUrl, "_blank", "noopener,noreferrer");
    },
    startImageRotation() {
      if (
        !Array.isArray(this.visualMediaItems) ||
        this.visualMediaItems.length <= 1 ||
        (this.currentVisualMediaType !== "image" && this.isCurrentVideoPlaying)
      ) {
        return;
      }

      this.clearImageRotation();
      this.imageRotationTimer = window.setInterval(() => {
        this.showNextImage();
      }, this.imageRotationInterval);
    },
    clearImageRotation() {
      if (this.imageRotationTimer) {
        window.clearInterval(this.imageRotationTimer);
        this.imageRotationTimer = null;
      }
    },
    showNextImage() {
      if (!Array.isArray(this.visualMediaItems) || this.visualMediaItems.length === 0) {
        return;
      }

      this.transitionName = "visual-slide-next";
      this.currentVisualImageIndex = this.getNextAvailableImageIndex(
        this.currentVisualImageIndex,
        1,
      );
    },
    goToImage(index) {
      if (
        !Array.isArray(this.visualMediaItems) ||
        index < 0 ||
        index >= this.visualMediaItems.length ||
        index === this.currentVisualImageIndex
      ) {
        return;
      }

      this.transitionName =
        index > this.currentVisualImageIndex
          ? "visual-slide-next"
          : "visual-slide-prev";
      this.currentVisualImageIndex = index;
    },
    getNextAvailableImageIndex(startIndex, step = 1) {
      const totalImages = this.visualMediaItems.length;

      for (let attempt = 1; attempt <= totalImages; attempt += 1) {
        const nextIndex = (startIndex + step * attempt + totalImages) % totalImages;
        if (!this.failedVisualImageIndexes.includes(nextIndex)) {
          return nextIndex;
        }
      }

      return startIndex;
    },
    handleVisualMediaError() {
      if (this.failedVisualImageIndexes.includes(this.currentVisualImageIndex)) {
        return;
      }

      this.failedVisualImageIndexes = [
        ...this.failedVisualImageIndexes,
        this.currentVisualImageIndex,
      ];

      if (this.failedVisualImageIndexes.length >= this.visualMediaItems.length) {
        this.clearImageRotation();
        return;
      }

      this.transitionName = "visual-slide-next";
      this.currentVisualImageIndex = this.getNextAvailableImageIndex(
        this.currentVisualImageIndex,
        1,
      );
    },
    submitForm(formName) {
      this.showBadCredential = false;
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.loadingButton = true;
          this.$store
              .dispatch("auth/login", this.credentials)
              .then(() => {
                this.$router.push("/dashboard");
                this.loginDeviceMethod();
              })
              .catch((err) => {
                setTimeout(() => {
                  this.showBadCredential = true;
                }, 500);
                this.loginError = err.response.data.message;
              })
              .finally(() => {
                setTimeout(() => {
                  this.loadingButton = false;
                }, 500);
              });
        } else {
          console.log("error validate data!!");
          return false;
        }
      });
    },
    loginDeviceMethod() {
      let deviceData = { idDevice: "" };
      deviceData.idDevice = this.$browserDetect.full.ua || window.navigator.userAgent;
      DeviceService.deviceLogin(deviceData)
          .then((resp) => {
            let data = resp.data.data;
            console.log(data);
          })
          .catch((err) => {
            console.log(err);
          });
    },
  },
};
</script>

<style lang="scss" scoped>
.split-screen-container {
  display: flex;
  height: 100vh;
  width: 100%;
  background-color: #fff;
  overflow: hidden;
}

/* KHỐI TRÁI: CHIẾM 2 PHẦN (66.6%) */
.visual-section {
  flex: 2;
  background-color: #f8fbff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;

  @media screen and (max-width: 1024px) {
    display: none;
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .visual-slider {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }

  .main-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  .main-video,
  .media-frame {
    width: 100%;
    height: 100%;
  }

  .main-video {
    object-fit: cover;
    object-position: center;
    display: block;
  }
}

.visual-indicators {
  position: absolute;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  z-index: 2;
}

.skip-video-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 3;
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  background: rgba(26, 84, 144, 0.85);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.skip-video-btn:hover {
  background: rgba(26, 84, 144, 0.95);
  transform: translateY(-1px);
}

.indicator-dot {
  width: 10px;
  height: 10px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  transition: all 0.25s ease;
  padding: 0;
}

.indicator-dot:hover {
  background: rgba(255, 255, 255, 0.82);
  transform: scale(1.08);
}

.indicator-dot.active {
  width: 28px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
}

.visual-slide-next-enter-active,
.visual-slide-next-leave-active,
.visual-slide-prev-enter-active,
.visual-slide-prev-leave-active {
  transition: opacity 0.55s ease, transform 0.55s ease;
}

.visual-slide-next-enter,
.visual-slide-prev-leave-to {
  opacity: 0;
  transform: translateX(40px);
}

.visual-slide-next-leave-to,
.visual-slide-prev-enter {
  opacity: 0;
  transform: translateX(-40px);
}

/* KHỐI PHẢI: CHIẾM 1 PHẦN (33.3%) */
.form-section {
  flex: 1;
  min-width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.03);
  z-index: 10;

  @media screen and (max-width: 1024px) {
    flex: 1;
    min-width: 100%;
  }
}

.form-container {
  width: 100%;
  max-width: 360px;
  padding: 0 20px;
}

/* BRAND HEADER: LOGO + TITLE */
.brand-header {
  text-align: center;
  margin-bottom: 30px;

  .login-logo {
    max-width: 220px;
    height: auto;
    margin-bottom: 18px;
    display: block;
    margin-left: auto;
    margin-right: auto;
    filter: drop-shadow(0 2px 8px rgba(85, 163, 247, 0.15));
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
}

.title-login {
  font-size: 28px;
  font-weight: 800;
  color: #1a5490;
  letter-spacing: 2px;
  margin: 0;
}

/deep/ .el-form--label-top .el-form-item__label {
  padding: 0 0 8px !important;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.6;
  font-size: 15px;
}

/deep/ .el-input__inner {
  height: 48px;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s;
  font-size: 15px;
  &:focus {
    border-color: #55a3f7;
    box-shadow: 0 0 0 2px rgba(85, 163, 247, 0.15);
  }
}

/deep/ .el-input__prefix {
  font-size: 16px;
}

/deep/ .el-input__inner::placeholder {
  font-size: 14px;
  color: #a8b5c4;
}

.app-type-selector {
  margin: 22px 0;
  /deep/ .el-form-item__content {
    text-align: center;
    line-height: 1;
  }
  /deep/ .el-radio {
    margin-right: 12px;
    .el-radio__label {
      padding-left: 6px;
      font-size: 15px;
      font-weight: 500;
      color: #2c3e50;
    }
  }
}

.error {
  font-size: 15px;
  text-align: center;
  margin-bottom: 16px;
  color: #ea4e4e;
  min-height: 22px;
  font-weight: 500;
}

.submit-btn {
  width: 100%;
  height: 50px;
  font-size: 17px;
  font-weight: 700;
  background-color: #55a3f7;
  border: none;
  box-shadow: 0 4px 14px rgba(85, 163, 247, 0.3);

  &:hover {
    background-color: #409eff;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(85, 163, 247, 0.4);
  }
}

.note {
  margin-top: 42px;
  text-align: center;

  .forgot-link {
    font-size: 14px;
    color: #5a7a9e;
    cursor: pointer;
    font-weight: 500;
    &:hover {
      color: #55a3f7;
      text-decoration: underline;
    }
  }

  .form-hr {
    border: 0;
    border-top: 1px solid #e6f0ff;
    margin: 22px auto;
    width: 80%;
  }

  .slogan-text {
    font-size: 13px;
    color: #7a8fa3;
    font-style: italic;
    font-weight: 500;
  }
}

.social-links-wrapper {
  margin-top: 24px;
  text-align: center;
}

.social-links-title {
  display: inline-block;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #7a8fa3;
}

.social-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.social-link {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dbe7f6;
  border-radius: 50%;
  background: #ffffff;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(38, 86, 132, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  padding: 0;
}

.social-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 22px rgba(38, 86, 132, 0.14);
}

.social-link--onekids {
  padding: 8px;
}

.social-link--facebook {
  color: #1877f2;
}

.social-link--zalo {
  color: #0068ff;
}

.social-link--youtube {
  color: #ff0000;
}

.social-link-logo {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.social-icon-svg {
  width: 24px;
  height: 24px;
}

.social-icon-svg--zalo {
  width: 28px;
  height: 28px;
}

::v-deep .zalo-contact-popper {
  border-radius: 18px;
  border: 1px solid #dbe7f6;
  box-shadow: 0 18px 36px rgba(38, 86, 132, 0.14);
  padding: 16px;
}

.zalo-contact-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.zalo-contact-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e9f3ff, #f7fbff);
}

.zalo-contact-header-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, #0d7dff, #38a0ff);
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(13, 125, 255, 0.22);
}

.zalo-header-svg {
  width: 24px;
  height: 24px;
}

.zalo-contact-header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.zalo-contact-title {
  font-size: 14px;
  font-weight: 700;
  color: #2c3e50;
}

.zalo-contact-subtitle {
  font-size: 12px;
  line-height: 1.45;
  color: #6b7f98;
}

.zalo-contact-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e3ecf8;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffffff, #f8fbff);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}

.zalo-contact-item:hover {
  background: #eef6ff;
  border-color: #8ebeff;
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(56, 123, 199, 0.1);
}

.zalo-contact-avatar {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #0d7dff, #49b2ff);
  color: #ffffff;
}

.zalo-contact-avatar-icon {
  width: 20px;
  height: 20px;
}

.zalo-contact-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.zalo-contact-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f334d;
}

.zalo-contact-phone {
  font-size: 12px;
  color: #6d819b;
}

.zalo-contact-action {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #edf5ff;
  color: #4c83c8;
}

.zalo-contact-action-icon {
  width: 16px;
  height: 16px;
}

/* Khử spin button của input số */
/deep/ input::-webkit-outer-spin-button,
/deep/ input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.app-download-wrapper {
  margin-top: 24px;
  text-align: center;
}

.app-download-title {
  display: inline-block;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #7a8fa3;
}

.app-download-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.app-download-link {
  min-width: 142px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #dbe7f6;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffffff, #f8fbff);
  box-shadow: 0 8px 18px rgba(38, 86, 132, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  text-align: left;
}

.app-download-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 22px rgba(38, 86, 132, 0.14);
  border-color: #9ec4fb;
}

.app-download-link--play {
  color: #138a4d;
}

.app-download-link--apple {
  color: #202733;
}

.app-download-icon {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #eef5ff;
}

.store-icon-svg {
  width: 20px;
  height: 20px;
}

.app-download-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.app-download-sub {
  font-size: 11px;
  color: #7f92aa;
}

.app-download-name {
  font-size: 14px;
  font-weight: 700;
  color: #22344d;
}

.copyright-link {
  display: block;
  width: 100%;
  margin-top: 18px;
  border: none;
  background: transparent;
  font-size: 12px;
  line-height: 1.6;
  color: #7f92aa;
  cursor: pointer;
  text-align: center;
  transition: color 0.2s ease;
}

.copyright-link:hover {
  color: #3f84e5;
  text-decoration: underline;
}

@media screen and (max-width: 480px) {
  .app-download-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .app-download-link {
    width: 100%;
    min-width: auto;
  }
}
</style>

